import { logger } from '../../utils/logger'
import { type LocalEmbedEventEmitter } from '../event-handler'
import { type PlayerJsContext } from '../hooks/playerjs'

/**
 * Communication interface between the embedder and the embedded experience engine.
 */
export interface ExperienceProtocol {
  /**
   * Resolves once the player.js is ready
   */
  waitForReady(): Promise<void>
  on (event: string, handler: (data: any) => void): void
  off (event: string, handler: (data: any) => void): void

  /**
   * Sends a message to the embedded experience engine.
   * @param method
   * @param value
   */
  send (method: string, value?: any): void

  /**
   * Sends a message to the embedded experience engine and waits for a response.
   * @param method
   * @param value
   */
  call<T = any> (method: string, value?: any): Promise<T>
}

/**
 * Implmentation of the ExperienceProtocol via the player.js
 */
export class PlayerjsExperienceProtocol implements ExperienceProtocol {
  eventEmitter: LocalEmbedEventEmitter
  playerjs?: PlayerJsContext
  isReady = false
  /**
   * resolved once the player.js is ready
   */
  #waitForReady: Promise<void>
  constructor (eventEmitter: LocalEmbedEventEmitter) {
    this.eventEmitter = eventEmitter
    this.#waitForReady = new Promise((resolve) => {
      this.eventEmitter.on('playerjs.ready', (playerjsContext: PlayerJsContext) => {
        this.playerjs = playerjsContext
        this.isReady = true
        resolve()
      })
    })
  }

  async waitForReady () {
    await this.#waitForReady
  }

  on (event: string, handler: (data: any) => void) {
    this.eventEmitter.on('playerjs.' + event, handler)
  }

  off (event: string, handler: (data: any) => void) {
    this.eventEmitter.off('playerjs.' + event, handler)
  }

  send (method: string, value?: any) {
    if (this.playerjs == null) {
      logger.warn('playerjs is not available yet. Ignoring send. If needed, await `waitForReady()`', method, value)
      return
    }
    this.playerjs?.player.send({ method, value })
  }

  async call<T = any> (method: string, value?: any): Promise<T> {
    await this.waitForReady()
    return await (new Promise((resolve) => {
      return this.playerjs?.player.send({ method, value }, (data: T) => {
        resolve(data)
      })
    }))
  }
}
